import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdAdd, MdToggleOn, MdToggleOff, MdDelete, MdLocalOffer, MdClose } from 'react-icons/md';

function AdminPromoCodes() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: '', discountType: 'percentage', discountValue: '', maxRedemptions: '', appliesTo: 'both', expiresAt: '',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadCodes(); }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/promo-codes`);
      setPromoCodes(res.data.promoCodes || []);
    } catch (err) {
      console.error('Failed to load promo codes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      // Convert the YYYY-MM-DD date to end-of-day ISO so the code is valid
      // through the entire selected date (rather than expiring at midnight UTC).
      const expiresAtISO = form.expiresAt
        ? new Date(`${form.expiresAt}T23:59:59`).toISOString()
        : null;
      await axios.post(`${API_URL}/api/admin/promo-codes`, {
        code: form.code,
        discount_type: form.discountType,
        discount_value: parseFloat(form.discountValue),
        max_redemptions: parseInt(form.maxRedemptions),
        applies_to: form.appliesTo,
        expires_at: expiresAtISO,
      });
      setShowCreate(false);
      setForm({ code: '', discountType: 'percentage', discountValue: '', maxRedemptions: '', appliesTo: 'both', expiresAt: '' });
      loadCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreating(false);
    }
  };

  const toggleCode = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/promo-codes/${id}/toggle`);
      loadCodes();
    } catch (err) {
      alert('Failed to toggle promo code');
    }
  };

  const deleteCode = async (id, code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${id}`);
      loadCodes();
    } catch (err) {
      alert('Failed to delete promo code');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-promo-codes-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
          <MdLocalOffer className="text-primary" />
          Promo Codes
        </h2>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center gap-1 transition-colors"
          data-testid="create-promo-btn"
        >
          <MdAdd /> New Promo Code
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="create-promo-modal">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-800 font-rubik">Create Promo Code</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <MdClose className="text-2xl" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Code</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. WELCOME20"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  required
                  data-testid="promo-code-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Discount Type</label>
                  <select
                    value={form.discountType}
                    onChange={e => setForm({ ...form, discountType: e.target.value, discountValue: '' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="promo-type-select"
                  >
                    <option value="percentage">Percentage Off</option>
                    <option value="free_trial">Free Trial (days)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">
                    {form.discountType === 'percentage' ? 'Discount (%)' : 'Trial Days'}
                  </label>
                  <input
                    type="number"
                    value={form.discountValue}
                    onChange={e => setForm({ ...form, discountValue: e.target.value })}
                    placeholder={form.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 30'}
                    min="1"
                    max={form.discountType === 'percentage' ? '100' : '365'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    required
                    data-testid="promo-value-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Applies To</label>
                  <select
                    value={form.appliesTo}
                    onChange={e => setForm({ ...form, appliesTo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="promo-applies-select"
                  >
                    <option value="both">Both</option>
                    <option value="subscriptions">Subscriptions Only</option>
                    <option value="assessments">Assessments Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Max Redemptions</label>
                  <input
                    type="number"
                    value={form.maxRedemptions}
                    onChange={e => setForm({ ...form, maxRedemptions: e.target.value })}
                    placeholder="e.g. 100"
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    required
                    data-testid="promo-max-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Expiry Date (optional)</label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={e => setForm({ ...form, expiresAt: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="promo-expiry-input"
                />
                <p className="text-xs text-gray-500 mt-1 font-rubik">
                  The code will be valid through the end of the selected day (23:59 local time). Leave empty for no expiry.
                </p>
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 font-rubik">{error}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={creating}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                data-testid="submit-promo-btn"
              >
                {creating ? 'Creating...' : 'Create Promo Code'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Promo Codes Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : promoCodes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12">
          <MdLocalOffer className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-rubik">No promo codes yet</p>
          <p className="text-gray-400 text-sm font-rubik mt-2">Create your first promo code to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="promo-codes-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Applies To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Redemptions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase font-rubik">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase font-rubik">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {promoCodes.map(code => (
                <tr key={code.id} className="hover:bg-gray-50" data-testid={`promo-row-${code.code}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded text-sm">{code.code}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-rubik">
                    {code.discountType === 'percentage'
                      ? `${code.discountValue}% off`
                      : `${code.discountValue} day trial`}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      code.appliesTo === 'both' ? 'bg-purple-100 text-purple-700' :
                      code.appliesTo === 'subscriptions' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {code.appliesTo === 'both' ? 'All' : code.appliesTo === 'subscriptions' ? 'Subscriptions' : 'Assessments'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-rubik">
                    {code.currentRedemptions} / {code.maxRedemptions}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-rubik">
                    {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      code.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {code.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleCode(code.id)}
                        className="text-gray-500 hover:text-primary transition-colors"
                        title={code.isActive ? 'Deactivate' : 'Activate'}
                        data-testid={`toggle-promo-${code.code}`}
                      >
                        {code.isActive ? <MdToggleOn className="text-2xl text-green-500" /> : <MdToggleOff className="text-2xl" />}
                      </button>
                      <button
                        onClick={() => deleteCode(code.id, code.code)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete"
                        data-testid={`delete-promo-${code.code}`}
                      >
                        <MdDelete className="text-xl" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminPromoCodes;
